//* Import the from libraries
import { useState, useCallback, useEffect } from "react";

//* Import hooks from react-router-dom
import { useNavigate } from "react-router-dom";

//* Import the List component
import { List } from "../../../component/list/list";

//* Import the Loading component
import { Loading } from "../../../component/common/Loading";

//* Import the useTests hook
import { useTests } from "../../../hooks/test/useTests";

//* Import the AuthAPI service
import { AuthAPI } from "../../../services/apis/AuthAPI";

//* Import the UserRole enum
import { UserRole } from "../../../constants/enum/UserRole";

//? Import the Types Test and User
import { Test } from "../../../hooks/test/types/types";

type User = {
  role: UserRole;
};

var debounce = require("lodash.debounce");

export function ExamView() {
  const token = localStorage.getItem("token") || "";
  const navigate = useNavigate();

  const [me, setMe] = useState<User | null>(null);
  const [showWarning, setShowWarning] = useState<boolean>(false);
  const [testToDelete, setTestToDelete] = useState<string | null>(null);
  const [searchInputValue, setSearchInputValue] = useState<string>("");
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [totalTests, setTotalTests] = useState<number>(0);

  const {
    test, // Recebendo 'tests' do hook
    loading,
    error,
    setSearch,
    pagination,
    setPagination,
    deleteTests,
    refetch,
    changePage,
    isDeleting,
  } = useTests(token);


  useEffect(() => {
    async function fetchMe() {
      try {
        const response = await AuthAPI.me(token);
        setMe(response.data);
      } catch (error) {
        console.error("Error fetching user details:", error);
      }
    }
    fetchMe();
  }, [token]);

  useEffect(() => {
    setTotalTests(pagination.totalItems || 0);
  }, [pagination.totalItems]);

  const debouncedSearch = useCallback(
    debounce((value: string) => {
      if (value.trim().length >= 3) {
        setSearch(value);
      } else {
        setSearch("");
        setPagination((prev) => ({ ...prev, currentPage: 1 }));
      }
      setIsSearching(false);
    }, 500),
    [setSearch, setPagination]
  );

  const handleSearchInputChange = useCallback(
    (value: string) => {
      setSearchInputValue(value);
      setIsSearching(true);
      debouncedSearch(value);
    },
    [debouncedSearch]
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (testToDelete) {
      try {
        const currentPageItems = test.length; // Corrigido para 'tests.length'
        const currentPage = pagination.currentPage;

        await deleteTests(testToDelete);

        if (currentPageItems === 1 && currentPage > 1) {
          changePage(currentPage - 1);
        }

        setShowWarning(false);
        setTestToDelete(null);
      } catch (error) {
        console.error("Error deleting test:", error);
      }
    }
  }, [
    testToDelete,
    deleteTests,
    test.length, // Corrigido para 'tests.length'
    pagination.currentPage,
    changePage,
  ]);

  const handleDeleteActionClick = useCallback((id: string) => {
    setTestToDelete(id);
    setShowWarning(true);
  }, []);

  const handleChangePagination = useCallback(
    (value: number) => {
      changePage(value);
    },
    [changePage]
  );

  if (!me || isDeleting) {
    return <Loading />;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <div className="row">
      <List
        search_input_value={searchInputValue}
        hideManager
        search_text="Pesquise pelo nome da provas"
        onChangeSearchInput={handleSearchInputChange}
        onSearchButtonClick={refetch}
        title={`Provas (Total: ${totalTests})`}
        not_found_message="Nenhum provas encontrado"
        button_link="/exams/create"
        button_text="Adicionar provas"
        deleting_message="Ao deletar este provas, ele não estará mais disponível no sistema"
        onDeleteCancel={() => setShowWarning(false)}
        onDeleteConfirm={handleDeleteConfirm}
        onDeleteActionClick={handleDeleteActionClick}
        onEditActionClick={(id) => navigate(`/exams/edit/${id}`)}
        onChangePagination={handleChangePagination}
        show_warning={showWarning}
        loading_screen={loading || isSearching}
        items={test.map((item: Test) => {
          const questionCount = item.questions ? item.questions.count : 0;

          return {
            ...item,
            key: item.id,
            questionCount,
          };
        })}
        pagination={{
          last_page: pagination.totalPages,
          current_page: pagination.currentPage,
        }}
        fields={[
          { name: "name", type: "text", header: "Nome" },
          { name: "createdBy", type: "text", header: "Cadastrado por" },
          { name: "createdAt", type: "date", header: "Criado em" },
          { name: "questionCount", type: "text", header: "Questões" },
        ]}
        loading_search={isSearching}
      />
    </div>
  );
}
